import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, X, Save, User, KeyRound, Copy, Eye, EyeOff, Lock, Unlock, Pencil } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { inputCls, labelCls, btnPrimary, btnDanger, PanelHeader } from "./_shared";
import ConfirmDialog from "@/components/site/ConfirmDialog";

// Generate a strong 14-char password (letters + digits + safe punctuation).
function generatePassword() {
  const chars =
    "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*";
  let out = "";
  const arr = new Uint32Array(14);
  window.crypto.getRandomValues(arr);
  for (let i = 0; i < 14; i++) out += chars[arr[i] % chars.length];
  return out;
}

export default function UsersPanel() {
  const { adminApi } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [resetShow, setResetShow] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [unlocking, setUnlocking] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.email || form.password.length < 8 || !form.name.trim()) {
      toast.error("Enter a name, valid email, and a password of 8+ characters.");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/users", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        phone: form.phone.trim(),
      });
      toast.success(`Admin ${form.name.trim()} created`);
      setShowNew(false);
      setForm({ email: "", password: "", name: "", phone: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ name: u.name || "", phone: u.phone || "" });
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditTarget(null);
    setEditForm({ name: "", phone: "" });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await adminApi.put(`/users/${encodeURIComponent(editTarget.email)}`, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
      });
      toast.success("Admin details updated");
      closeEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEditSaving(false);
    }
  };

  const unlock = async (u) => {
    setUnlocking((s) => ({ ...s, [u.email]: true }));
    try {
      const { data } = await adminApi.post(
        `/users/${encodeURIComponent(u.email)}/unlock`
      );
      if (data?.was_locked) {
        toast.success(`${u.email} unlocked — they can log in again.`);
      } else {
        toast.info(`${u.email} wasn't locked. Login attempts cleared just in case.`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUnlocking((s) => {
        const next = { ...s };
        delete next[u.email];
        return next;
      });
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    try {
      await adminApi.delete(`/users/${encodeURIComponent(pendingDelete.email)}`);
      toast.success("Admin removed");
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openReset = (u) => {
    setResetTarget(u);
    setResetPwd("");
    setResetShow(false);
  };

  const closeReset = () => {
    if (resetSaving) return;
    setResetTarget(null);
    setResetPwd("");
    setResetShow(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetTarget || resetPwd.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setResetSaving(true);
    try {
      await adminApi.post(
        `/users/${encodeURIComponent(resetTarget.email)}/reset-password`,
        { new_password: resetPwd }
      );
      toast.success(`Password reset for ${resetTarget.email}. Share it securely.`);
      // Auto-copy so the admin can send it to the user without exposing on screen
      try {
        await navigator.clipboard.writeText(resetPwd);
        toast.info("New password copied to clipboard.");
      } catch {
        /* clipboard may be blocked; UI still shows the password */
      }
      setResetTarget(null);
      setResetPwd("");
      setResetShow(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetSaving(false);
    }
  };

  return (
    <div data-testid="users-panel">
      <PanelHeader
        eyebrow="09"
        title="Admin Users"
        description="Anyone in this list can log in to the admin panel with their email and password."
        actions={
          <button data-testid="users-add" onClick={() => setShowNew(true)} className={btnPrimary}>
            <Plus size={14} /> Add Admin
          </button>
        }
      />

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-white/10 bg-[#1A1D21] p-10 text-center text-white/50">No admins yet.</div>
      ) : (
        <div className="space-y-3">
          {items.map((u) => (
            <div key={u.email} data-testid={`user-row-${u.email}`} className="border border-white/10 bg-[#1A1D21] p-5 flex flex-wrap items-center gap-4">
              <div className="w-10 h-10 bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                <User size={18} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div data-testid={`user-name-${u.email}`} className="font-display text-lg tracking-wide truncate">
                    {u.name?.trim() || u.email}
                  </div>
                  {u.is_you && (
                    <span className="text-[10px] tracking-widest uppercase text-[#FF6B00] border border-[#FF6B00]/40 px-2 py-0.5">
                      You
                    </span>
                  )}
                  {u.is_locked && (
                    <span
                      data-testid={`user-locked-${u.email}`}
                      className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-red-400 border border-red-400/40 px-2 py-0.5"
                    >
                      <Lock size={9} /> Locked
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/50 mt-1 space-y-0.5">
                  {u.name?.trim() && <div className="truncate">{u.email}</div>}
                  {u.phone?.trim() && <div data-testid={`user-phone-${u.email}`}>{u.phone}</div>}
                  {u.created_at && <div>Added {new Date(u.created_at).toLocaleDateString()}</div>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {u.is_locked && (
                  <button
                    onClick={() => unlock(u)}
                    disabled={!!unlocking[u.email]}
                    data-testid={`user-unlock-${u.email}`}
                    title="Clear failed login attempts so this admin can log in again"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#FF6B00] hover:bg-[#E65A00] disabled:opacity-40 font-display tracking-widest uppercase text-xs text-white transition-colors"
                  >
                    {unlocking[u.email] ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />}
                    Unlock
                  </button>
                )}
                <button
                  onClick={() => openEdit(u)}
                  data-testid={`user-edit-${u.email}`}
                  title="Edit name and phone number"
                  className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] font-display tracking-widest uppercase text-xs text-white transition-colors"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => openReset(u)}
                  disabled={u.is_you}
                  data-testid={`user-reset-${u.email}`}
                  title={u.is_you ? "Use 'My Account → Change Password' to reset your own password" : "Reset this admin's password"}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-[#FF6B00] hover:text-[#FF6B00] font-display tracking-widest uppercase text-xs text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <KeyRound size={12} /> Reset Password
                </button>
                <button
                  onClick={() => setPendingDelete(u)}
                  disabled={u.is_you}
                  data-testid={`user-delete-${u.email}`}
                  title={u.is_you ? "You can't delete your own account" : "Remove this admin"}
                  className={`${btnDanger} disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div
          data-testid="user-new-modal"
          className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && setShowNew(false)}
        >
          <form onSubmit={create} className="w-full max-w-md bg-[#1A1D21] border border-white/10 my-8">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div className="font-display uppercase tracking-wider text-xl">Add Admin</div>
              <button type="button" onClick={() => setShowNew(false)} className="text-white/60 hover:text-white" aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  data-testid="user-new-name"
                  className={inputCls}
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  minLength={1}
                  maxLength={80}
                />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input
                  data-testid="user-new-email"
                  className={inputCls}
                  type="email"
                  autoComplete="off"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className={labelCls}>Phone (optional)</label>
                <input
                  data-testid="user-new-phone"
                  className={inputCls}
                  type="tel"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  maxLength={40}
                />
              </div>
              <div>
                <label className={labelCls}>Password (min 8 characters)</label>
                <input
                  data-testid="user-new-password"
                  className={inputCls}
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  required
                  minLength={8}
                />
                <p className="text-xs text-white/40 mt-2">
                  Share these credentials with the new admin securely (they can change their own password after logging in).
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              <button type="submit" disabled={saving} data-testid="user-new-submit" className={btnPrimary}>
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create Admin
              </button>
            </div>
          </form>
        </div>
      )}

      {editTarget && (
        <div
          data-testid="user-edit-modal"
          className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && closeEdit()}
        >
          <form onSubmit={submitEdit} className="w-full max-w-md bg-[#1A1D21] border border-white/10 my-8">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <div className="font-display uppercase tracking-wider text-xl">Edit Admin</div>
                <div className="text-xs text-white/50 mt-1 truncate">{editTarget.email}</div>
              </div>
              <button type="button" onClick={closeEdit} className="text-white/60 hover:text-white" aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  data-testid="user-edit-name"
                  className={inputCls}
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  minLength={1}
                  maxLength={80}
                />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input
                  data-testid="user-edit-phone"
                  className={inputCls}
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  maxLength={40}
                />
              </div>
              <p className="text-xs text-white/40">
                Email and password can't be changed here — use "Reset Password" to change the password, or remove and recreate to change the email.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
              <button type="button" onClick={closeEdit} disabled={editSaving} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
              <button type="submit" disabled={editSaving} data-testid="user-edit-submit" className={btnPrimary}>
                {editSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </form>
        </div>
      )}

      {resetTarget && (
        <div
          data-testid="user-reset-modal"
          className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && closeReset()}
        >
          <form onSubmit={submitReset} className="w-full max-w-md bg-[#1A1D21] border border-white/10 my-8">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <div className="font-display uppercase tracking-wider text-xl">Reset Password</div>
                <div className="text-xs text-white/50 mt-1 truncate">{resetTarget.email}</div>
              </div>
              <button
                type="button"
                onClick={closeReset}
                className="text-white/60 hover:text-white"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className={labelCls}>New Password (min 8 characters)</label>
                <div className="relative">
                  <input
                    data-testid="user-reset-password"
                    className={`${inputCls} pr-24`}
                    type={resetShow ? "text" : "password"}
                    autoComplete="new-password"
                    value={resetPwd}
                    onChange={(e) => setResetPwd(e.target.value)}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setResetShow((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white/50 hover:text-[#FF6B00] p-2"
                    aria-label={resetShow ? "Hide password" : "Show password"}
                    data-testid="user-reset-toggle-visibility"
                  >
                    {resetShow ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      const p = generatePassword();
                      setResetPwd(p);
                      setResetShow(true);
                    }}
                    data-testid="user-reset-generate"
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-[#FF6B00] hover:text-[#FF6B00] text-white text-xs font-display tracking-widest uppercase transition-colors"
                  >
                    Generate Strong
                  </button>
                  <button
                    type="button"
                    disabled={!resetPwd}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(resetPwd);
                        toast.success("Password copied to clipboard");
                      } catch {
                        toast.error("Clipboard blocked — copy manually.");
                      }
                    }}
                    data-testid="user-reset-copy"
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 hover:border-[#FF6B00] hover:text-[#FF6B00] text-white text-xs font-display tracking-widest uppercase disabled:opacity-40 transition-colors"
                  >
                    <Copy size={12} /> Copy
                  </button>
                </div>
                <p className="text-xs text-white/40 mt-3 leading-relaxed">
                  This will overwrite the admin's current password. Send them the new one securely (encrypted message, in-person, etc.) — they can change it themselves after logging in.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
              <button
                type="button"
                onClick={closeReset}
                disabled={resetSaving}
                className="px-4 py-2 text-sm text-white/60 hover:text-white"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={resetSaving || resetPwd.length < 8}
                data-testid="user-reset-submit"
                className={btnPrimary}
              >
                {resetSaving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Reset Password
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Remove this admin?"
        description={pendingDelete ? `${pendingDelete.email} will no longer be able to log in.` : ""}
        confirmLabel="Remove Admin"
        onConfirm={remove}
        testid="user-delete-dialog"
      />
    </div>
  );
}
